import api from '.';
import { ORDER } from '../common/enum/order';
import {
  CountStatus,
  DocumentRevisionDashboard,
  Project
} from '../pages/Project/projectSlice';
import { ApprovalPeriod } from '../pages/ProjectApprovalPeriod/projectApprovalPeriodSlice';

export interface GetProjects {
  page: number;
  limit: number;
  sortBy?: string;
  orderBy?: ORDER;
  search?: string | null;
}

export interface GetAllMyTaskProjects {
  sortBy?: string;
  orderBy?: ORDER;
}

interface CreateProject {
  projectRegisterId: number;
  projectAccessControlUsers: {
    userId: number;
    accessControlIds: number[];
  }[];
  approvalPeriods: ApprovalPeriod[];
}

export interface CreatePlugAndPlayProject {
  name: string;
  projectNo: string;
  plantName: string;
  customerCompanyName: string;
  productName: string;
  businessUnitName: string;
  businessUnitGroupName: string;
  projectStatus: string;
  ndaExpireDate?: string;
  projectAccessControlUsers: {
    userId: number;
    accessControlIds: number[];
  }[];
  // approvalPeriods: ApprovalPeriod[];
}

export interface UpdateProject extends Omit<Project, 'users'> { }

export enum CountBy {
  issuedDate = 'issue-date',
  completedDate = 'completed-date'
}

interface GetCountDocumentRevisions {
  projectId: number;
  countBy: CountBy;
  disciplineIds: number[];
  purposeIds: number[];
  startDate: Date;
  endDate: Date;
}

interface GetProjectDashboardInformation {
  projectId: number;
  disciplineIds: number[];
  purposeIds: number[];
  startDate: Date;
  endDate: Date;
}

interface GetSumProjectProgress {
  projectId: number;
  disciplineIds: number[];
  purposeIds: number[];
}

interface GetDashboardDocumentRevisionStatus
  extends GetProjectDashboardInformation {
  search: string | null;
  today: Date;
}

interface ReturnGetDashboardDocumentRevisionStatusType {
  data: CountStatus;
}

interface GetDashboardDocumentRevision extends GetProjectDashboardInformation {
  page: number;
  limit: number;
  status: string | null;
  search: string | null;
}

interface ReturnGetDashboardDocumentRevisionType {
  count: number;
  data: DocumentRevisionDashboard[];
}

interface ReturnGetCountDocumentRevisionsType {
  data: { month: string; plan: number; actual: number }[];
}

interface ReturnGetSumProjectProgresses
  extends ReturnGetCountDocumentRevisionsType {
  sumTotalManHours: number;
}

export async function getProjects({
  page,
  limit,
  sortBy,
  orderBy,
  search
}: GetProjects) {
  const params = {
    page,
    limit,
    sortBy,
    orderBy,
    search
  };
  const { data } = await api.get('/projects', { params });
  return data;
}

export async function getAllMyTaskProjects({
  sortBy,
  orderBy
}: GetAllMyTaskProjects) {
  const params = {
    sortBy,
    orderBy
  };
  const { data } = await api.get('/projects/all-my-tasks', { params });
  return data;
}

export async function getProject(id: number) {
  const { data } = await api.get(`/projects/${id}`);
  return data;
}

export async function getBookmarkProjects(bookmarkIds: number[]) {
  const params = bookmarkIds
    .map((id) => {
      return 'id=' + id;
    })
    .join('&');
  const { data } = await api.get('/projects?' + params);
  return data;
}

export async function createProject({
  projectRegisterId,
  projectAccessControlUsers,
  approvalPeriods
}: CreateProject) {
  const createProject = {
    projectRegisterId,
    projectAccessControlUsers,
    approvalPeriods
  };
  const { data } = await api.post('/projects', createProject);
  return data;
}

export async function createPlugAndPlayProject({
  name,
  projectNo,
  plantName,
  customerCompanyName,
  productName,
  businessUnitName,
  businessUnitGroupName,
  projectStatus,
  ndaExpireDate,
  projectAccessControlUsers,
  // approvalPeriods
}: CreatePlugAndPlayProject) {
  const createProjectData = {
    name,
    projectNo,
    plantName,
    customerCompanyName,
    productName,
    businessUnitName,
    businessUnitGroupName,
    projectStatus,
    ndaExpireDate,
    projectAccessControlUsers,
    // approvalPeriods
  };
  const { data } = await api.post('/projects', createProjectData);
  return data;
}

export async function updateProject(updateProject: UpdateProject) {
  const { data } = await api.put(
    `/projects/${updateProject.id}`,
    updateProject
  );
  return data;
}

export async function getCountDocumentRevisions(
  params: GetCountDocumentRevisions
): Promise<ReturnGetCountDocumentRevisionsType> {
  const { data } = await api.get(
    `projects/${params.projectId}/count-document-revisions`,
    {
      params
    }
  );
  return data;
}

export async function getDocumentRevisionStatuses(
  params: GetDashboardDocumentRevisionStatus
): Promise<ReturnGetDashboardDocumentRevisionStatusType> {
  const { data } = await api.get(
    `/projects/${params.projectId}/document-revision-statuses`,
    {
      params
    }
  );
  return data;
}

export async function getDocumentRevisions(
  params: GetDashboardDocumentRevision
): Promise<ReturnGetDashboardDocumentRevisionType> {
  const { data } = await api.get(
    `/projects/${params.projectId}/project-document-revisions`,
    {
      params
    }
  );
  return data;
}

export async function getSumProjectProgresses(
  params: GetSumProjectProgress
): Promise<ReturnGetSumProjectProgresses> {
  const { data } = await api.get(
    `/projects/${params.projectId}/sum-project-progresses`,
    {
      params
    }
  );
  return data;
}

export async function findUsersInProject(projectId: number) {
  const { data } = await api.get(`/projects/${projectId}/users`);
  return data;
}
